import Component from '@glimmer/component';
import { getOwner } from '@ember/owner';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import {
  HdsModal,
  HdsTextBody,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';

/**
 *
 * `IamGroupsModalsRemoveGroup` renders the modal for removing a member from an HCP Group
 *
 *
 * ```
 * <Iam::Groups::Modals::RemoveMember
 *  @group={{@group}}
 *  @memberToRemove={{@memberToRemove}}
 *  @isActive={{this.isActive}}
 *  @closeModal={{this.closeRemoveModal}}
 * />
 * ```
 *
 * @class IamGroupsModalsRemoveGroup
 *
 */

/**
 * `group` is an object that contains the HCP Group properties
 * @argument group
 * @type {Object}
 */

/**
 * `memberToRemove is an object that contains properties of the member to remove from the HCP Group
 * @argument memberToRemove
 * @type {Object}
 */

/**
 * This shows or hides the modal based on truthiness.
 * @argument isActive
 * @type {Boolean}
 */

/**
 * A callback function to be called on a successful removal of a group member or
 * owhen a user cancels the modal.
 * @argument closeModal
 * @type {Function}
 */
export default class IamGroupsModalsRemoveGroup extends Component {
  @service api;
  @service intl;
  @service flashMessages;
  @service router;
  @service userContext;

  updateGroupMembers = task(async () => {
    let resourceName = this.args.group.resourceName;

    let payload = {
      resourceName,
      memberPrincipalIdsToAdd: [],
      memberPrincipalIdsToRemove: [this.args.memberToRemove.id],
    };

    let flashMessageDetails = {
      groupName: this.args.group.displayName,
      memberNameOrEmail:
        this.args.memberToRemove.name || this.args.memberToRemove.email,
      htmlSafe: true,
    };

    try {
      await this.api.groups.groupsServiceUpdateGroupMembers(
        resourceName,
        payload
      );

      const successTitle = this.intl.t(
        'components.iam.groups.form.remove-member.success.title'
      );

      this.flashMessages.success(successTitle, {
        content: this.intl.t(
          'components.iam.groups.form.remove-member.success.message',
          flashMessageDetails
        ),
      });

      //refresh the Group detail and list routes to update the model
      const detailRoute = getOwner(this).lookup(
        'route:cloud.access-control.groups.detail'
      );
      const listRoute = getOwner(this).lookup(
        'route:cloud.access-control.groups.list'
      );

      detailRoute.refresh();
      listRoute.refresh();

      await this.args.closeModal();

      // transition to the Group detail route
      return this.router.transitionTo('cloud.access-control.groups.detail');
    } catch {
      const errorTitle = this.intl.t(
        'components.iam.groups.form.remove-member.error.title'
      );
      this.flashMessages.error(errorTitle, {
        content: this.intl.t(
          'components.iam.groups.form.remove-member.error.message',
          flashMessageDetails
        ),
      });
      return this.router.transitionTo('cloud.access-control.groups.detail');
    }
  });

  <template>
    {{#if @isActive}}
      <HdsModal
        @color="critical"
        @onClose={{@closeModal}}
        data-test-iam-groups-modal-remove-member
        as |M|
      >
        <M.Header
          @icon="alert-triangle"
          @tagline={{this.headerTagline}}
          data-test-iam-groups-modal-remove-member-header
        >
          {{t
            "components.iam.groups.modals.remove-member.header.title"
            memberNameOrEmail=(or @memberToRemove.name @memberToRemove.email)
            groupName=@group.displayName
            htmlSafe=true
          }}
        </M.Header>
        <M.Body>
          <form
            id="update-group-members-form"
            {{on "submit" this.updateGroupMembers.perform}}
            data-test-iam-groups-modal-remove-member-body
          >
            <HdsTextBody @color="primary" @weight="regular">
              {{t
                "components.iam.groups.modals.remove-member.body"
                memberNameOrEmail=(or
                  @memberToRemove.name @memberToRemove.email
                )
                groupName=@group.displayName
                orgName=this.userContext.organization.name
                htmlSafe=true
              }}
            </HdsTextBody>
          </form>
        </M.Body>
        <M.Footer>
          <HdsButtonSet>
            <HdsButton
              @color="critical"
              @text={{t "components.iam.groups.form.remove-member.cta"}}
              disabled={{this.updateGroupMembers.isRunning}}
              form="update-group-members-form"
              type="submit"
              data-test-iam-groups-modal-remove-member-submit
            />
            <HdsButton
              @color="secondary"
              @text={{t "components.form.cancel"}}
              disabled={{this.updateGroupMembers.isRunning}}
              {{on "click" @closeModal}}
              data-test-iam-groups-modal-remove-member-cancel
            />
          </HdsButtonSet>
        </M.Footer>
      </HdsModal>
    {{/if}}
  </template>
}
